import { useState, useEffect } from 'react';
import SparkApproval from '../components/SparkApproval';
import './Circuits.css';

const EDGE_URL = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';

// Circuit lifecycle: Pending → Approved → Deployed → Retired
type CircuitStatus = 'pending' | 'approved' | 'deployed' | 'retired';
type Tab = CircuitStatus | 'templates';

// Circuit template categories
type CircuitCategory = 'consensus' | 'crypto' | 'governance' | 'network' | 'subscription' | 'sync' | 'wire' | 'witness';

interface CircuitTemplate {
  id: string;
  name: string;
  category: CircuitCategory;
  description: string;
  version: string;
  luts?: number;
  fmax?: number;
  notes?: string;
  testsPassing?: boolean;
}

// Available circuit templates from the platform
const CIRCUIT_TEMPLATES: CircuitTemplate[] = [
  // Consensus
  { id: 'dag-order', name: 'DAG Order', category: 'consensus', description: 'DAG ordering for event sequencing', version: '1.0.0', testsPassing: true },
  { id: 'vote-validator', name: 'Vote Validator', category: 'consensus', description: 'Validates consensus votes', version: '1.0.0', testsPassing: true },
  
  // Crypto
  { id: 'mldsa87-verify', name: 'ML-DSA-87 Verify', category: 'crypto', description: 'Post-quantum signature verification (NIST L5)', version: '1.0.0', notes: 'NIST L5' },
  { id: 'ntt-engine', name: 'NTT Engine', category: 'crypto', description: 'Number-theoretic transform for lattice crypto', version: '1.0.0' },
  { id: 'pq-tls-handshake', name: 'PQ TLS Handshake', category: 'crypto', description: 'Hybrid ML-KEM TLS handshake', version: '1.0.0', notes: 'Hybrid ML-KEM' },
  { id: 'threshold-sign', name: 'Threshold Signatures', category: 'crypto', description: 'K-of-N threshold signing with Shamir', version: '1.0.0', notes: 'Shamir + Lagrange' },
  
  // Governance
  { id: 'guardrail-pipeline', name: 'Guardrail Pipeline', category: 'governance', description: 'Composed governance rule checks', version: '1.0.0', luts: 1200, fmax: 500 },
  { id: 'max-influence', name: 'Max Influence', category: 'governance', description: 'Whale cap enforcement for voting', version: '1.0.0', luts: 120, fmax: 500 },
  { id: 'min-participation', name: 'Min Participation', category: 'governance', description: 'Inclusion floor for proposals', version: '1.0.0', luts: 100, fmax: 500 },
  { id: 'reputation-modifier', name: 'Reputation Modifier', category: 'governance', description: 'Dynamic reputation adjustments', version: '1.0.0', luts: 80, fmax: 500 },
  
  // Network
  { id: 'rate-limiter', name: 'Rate Limiter', category: 'network', description: 'Request rate limiting', version: '1.0.0', testsPassing: true },
  { id: 'routing', name: 'Routing', category: 'network', description: 'Packet routing decisions', version: '1.0.0', testsPassing: true },
  
  // Subscription
  { id: 'filter', name: 'Filter', category: 'subscription', description: 'Server-side WHERE clause filtering', version: '1.0.0', luts: 250, fmax: 500, notes: '4 rules' },
  { id: 'aggregate', name: 'Aggregate', category: 'subscription', description: 'Time-window rollups (count/sum/min/max/avg)', version: '1.0.0', luts: 180, fmax: 500 },
  { id: 'privacy-mask', name: 'Privacy Mask', category: 'subscription', description: 'Field-level access control', version: '1.0.0', luts: 150, fmax: 500, notes: 'redact/hide/encrypt' },
  { id: 'throttle', name: 'Throttle', category: 'subscription', description: 'Client rate limiting', version: '1.0.0', luts: 100, fmax: 500, notes: 'fixed/sliding/debounce' },
  { id: 'pipeline', name: 'Pipeline', category: 'subscription', description: 'Composed subscription processing', version: '1.0.0', luts: 350, fmax: 500 },
  
  // Sync
  { id: 'conflict-resolver', name: 'Conflict Resolver', category: 'sync', description: 'Multi-device conflict resolution', version: '1.0.0', luts: 150, fmax: 500, notes: 'LWW/FWW/KeepBoth' },
  { id: 'queue-processor', name: 'Queue Processor', category: 'sync', description: 'Offline queue processing with backoff', version: '1.0.0', luts: 120, fmax: 500 },
  
  // Wire
  { id: 'header-parser', name: 'Header Parser', category: 'wire', description: 'Wire protocol header parsing', version: '1.0.0' },
  { id: 'header-validator', name: 'Header Validator', category: 'wire', description: 'Wire protocol header validation', version: '1.0.0' },
  
  // Witness
  { id: 'vrf-output-hash', name: 'VRF Output Hash', category: 'witness', description: 'Verifiable random function output hashing', version: '1.0.0' },
  { id: 'vrf-threshold-check', name: 'VRF Threshold Check', category: 'witness', description: 'VRF threshold validation', version: '1.0.0', testsPassing: true },
  { id: 'witness-aggregator', name: 'Witness Aggregator', category: 'witness', description: 'Aggregate witness attestations', version: '1.0.0', testsPassing: true },
  { id: 'witness-selection', name: 'Witness Selection', category: 'witness', description: 'Fair witness committee selection', version: '1.0.0', testsPassing: true },
];

interface Signature {
  circuitId: string;
  signature: string;
  signerKeyHash: string;
  publicKey?: string;
  algorithm: string;
  timestamp: number;
  verified?: boolean;
  approvedAt: string;
}

interface CircuitVersion {
  major: number;
  minor: number;
  patch: number;
  label?: string; // e.g., "alpha", "beta", "rc1"
}

interface Circuit {
  id: string;
  type: string;
  description: string;
  status: CircuitStatus;
  signatures: Signature[];
  requiredSignatures: number;
  createdAt: string;
  // Version tracking
  version?: CircuitVersion;
  latestVersion?: CircuitVersion; // If different from version, upgrade available
  deployedAt?: string;
  retiredAt?: string;
  // Node tracking
  nodeCount?: number; // Number of nodes running this circuit
  nodesUpToDate?: number; // Number of nodes with latest version
}

interface CircuitsData {
  pending: Circuit[];
  approved: Circuit[];
  deployed: Circuit[];
  retired: Circuit[];
}

// Helper to format version string
function formatVersion(v?: CircuitVersion): string {
  if (!v) return 'v0.0.0';
  const label = v.label ? `-${v.label}` : '';
  return `v${v.major}.${v.minor}.${v.patch}${label}`;
}

// Helper to check if upgrade is available
function hasUpgradeAvailable(circuit: Circuit): boolean {
  if (!circuit.version || !circuit.latestVersion) return false;
  const current = circuit.version;
  const latest = circuit.latestVersion;
  return (
    latest.major > current.major ||
    (latest.major === current.major && latest.minor > current.minor) ||
    (latest.major === current.major && latest.minor === current.minor && latest.patch > current.patch)
  );
}

// Map legacy status to new lifecycle
function normalizeStatus(status: string): CircuitStatus {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'approved':
    case 'completed': // Legacy status maps to approved
      return 'approved';
    case 'deployed':
    case 'active':
    case 'running':
      return 'deployed';
    case 'retired':
    case 'terminated':
    case 'cancelled':
      return 'retired';
    default:
      return 'pending';
  }
}

export default function Circuits() {
  const [activeTab, setActiveTab] = useState<Tab>('deployed');
  const [circuits, setCircuits] = useState<CircuitsData>({ 
    pending: [], 
    approved: [], 
    deployed: [], 
    retired: [] 
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCircuit, setSelectedCircuit] = useState<Circuit | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<CircuitTemplate | null>(null);
  const [templateFilter, setTemplateFilter] = useState<CircuitCategory | 'all'>('all');
  const [viewingSource, setViewingSource] = useState<CircuitTemplate | null>(null);
  
  // Fetch circuits via HTTP polling
  useEffect(() => {
    const fetchCircuits = async () => {
      try {
        const response = await fetch(`${EDGE_URL}/api/circuits`);
        if (!response.ok) throw new Error('Failed to fetch circuits');
        
        const data = await response.json();
        const allCircuits: Circuit[] = (data.circuits || []).map((c: Circuit & { status: string }) => ({
          ...c,
          status: normalizeStatus(c.status),
        }));
        
        setCircuits({
          pending: allCircuits.filter((c) => c.status === 'pending'),
          approved: allCircuits.filter((c) => c.status === 'approved'),
          deployed: allCircuits.filter((c) => c.status === 'deployed'),
          retired: allCircuits.filter((c) => c.status === 'retired'),
        });
        setLoading(false);
      } catch (e) {
        setError(String(e));
        setLoading(false);
      }
    };
    
    fetchCircuits();
    const interval = setInterval(fetchCircuits, 5000);
    return () => clearInterval(interval);
  }, []);
  
  const filteredTemplates = templateFilter === 'all' 
    ? CIRCUIT_TEMPLATES 
    : CIRCUIT_TEMPLATES.filter(t => t.category === templateFilter);
  
  const getCategoryLabel = (cat: CircuitCategory): string => {
    return cat.charAt(0).toUpperCase() + cat.slice(1);
  };
  
  const getCategoryColor = (cat: CircuitCategory): string => {
    switch (cat) {
      case 'consensus': return 'primary';
      case 'crypto': return 'success';
      case 'governance': return 'warning';
      case 'network': return 'info';
      case 'subscription': return 'secondary';
      case 'sync': return 'tertiary';
      case 'wire': return 'muted';
      case 'witness': return 'accent';
      default: return 'muted';
    }
  };
  
  return (
    <div className="circuits-page animate-in">
      {/* Spark Approval Modal */}
      {selectedCircuit && (
        <div className="spark-modal-overlay" onClick={() => setSelectedCircuit(null)}>
          <div onClick={e => e.stopPropagation()}>
            <SparkApproval
              circuitId={selectedCircuit.id}
              circuitType={selectedCircuit.type}
              description={selectedCircuit.description}
              onApproved={() => {
                setSelectedCircuit(null);
                // Refresh will happen via polling
              }}
              onClose={() => setSelectedCircuit(null)}
            />
          </div>
        </div>
      )}
      
      {/* Template Deploy Modal */}
      {selectedTemplate && (
        <TemplateDeployModal 
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
        />
      )}
      
      {/* Template Source View Modal */}
      {viewingSource && (
        <TemplateSourceModal 
          template={viewingSource}
          onClose={() => setViewingSource(null)}
          onDeploy={() => {
            setViewingSource(null);
            setSelectedTemplate(viewingSource);
          }}
        />
      )}
      
      {/* Header */}
      <div className="circuits-header">
        <div className="header-content">
          <h2>Smart Circuits</h2>
          <p className="header-subtitle">Deploy and manage programmable compute circuits</p>
        </div>
        <button className="btn-primary" onClick={() => setActiveTab('templates')}>
          + Deploy New Circuit
        </button>
      </div>
      
      {/* Tabs - Circuit Lifecycle: Templates → Pending → Approved → Deployed → Retired */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Templates
          <span className="tab-badge badge-info">{CIRCUIT_TEMPLATES.length}</span>
        </button>
        <button 
          className={`tab ${activeTab === 'deployed' ? 'active' : ''}`}
          onClick={() => setActiveTab('deployed')}
        >
          Deployed
          {circuits.deployed.some(hasUpgradeAvailable) && (
            <span className="tab-badge badge-warning" title="Upgrades available">↑</span>
          )}
        </button>
        <button 
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending
          {circuits.pending.length > 0 && (
            <span className="tab-badge">{circuits.pending.length}</span>
          )}
        </button>
        <button 
          className={`tab ${activeTab === 'approved' ? 'active' : ''}`}
          onClick={() => setActiveTab('approved')}
        >
          Approved
          {circuits.approved.length > 0 && (
            <span className="tab-badge badge-success">{circuits.approved.length}</span>
          )}
        </button>
        <button 
          className={`tab ${activeTab === 'retired' ? 'active' : ''}`}
          onClick={() => setActiveTab('retired')}
        >
          Retired
        </button>
      </div>
      
      {/* Content */}
      {activeTab === 'templates' ? (
        <div className="templates-content">
          {/* Category Filter */}
          <div className="category-filter">
            <button 
              className={`filter-btn ${templateFilter === 'all' ? 'active' : ''}`}
              onClick={() => setTemplateFilter('all')}
            >
              All
            </button>
            {(['consensus', 'crypto', 'governance', 'network', 'subscription', 'sync', 'wire', 'witness'] as CircuitCategory[]).map(cat => (
              <button
                key={cat}
                className={`filter-btn ${templateFilter === cat ? 'active' : ''}`}
                onClick={() => setTemplateFilter(cat)}
              >
                {getCategoryLabel(cat)}
              </button>
            ))}
          </div>
          
          {/* Templates Grid */}
          <div className="templates-grid">
            {filteredTemplates.map(template => (
              <div key={template.id} className="template-card">
                <div className="template-header">
                  <span className={`category-badge ${getCategoryColor(template.category)}`}>
                    {getCategoryLabel(template.category)}
                  </span>
                  {template.testsPassing && (
                    <span className="tests-badge" title="Tests passing">✓</span>
                  )}
                </div>
                <h3 className="template-name">{template.name}</h3>
                <p className="template-description">{template.description}</p>
                <div className="template-meta">
                  <span className="version">v{template.version}</span>
                  {template.luts && <span className="luts">{template.luts} LUTs</span>}
                  {template.notes && <span className="notes">{template.notes}</span>}
                </div>
                <div className="template-actions">
                  <button 
                    className="btn-view-source"
                    onClick={() => setViewingSource(template)}
                  >
                    View Source
                  </button>
                  <button 
                    className="btn-deploy"
                    onClick={() => setSelectedTemplate(template)}
                  >
                    Deploy
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="card">
          {loading ? (
            <div className="empty-state">
              <span className="empty-icon">⏳</span>
              <p>Loading circuits...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <span className="empty-icon">⚠️</span>
              <p>Error: {error}</p>
            </div>
          ) : (
            <>
              {activeTab === 'pending' && (
                <PendingCircuits 
                  circuits={circuits.pending} 
                  onApprove={setSelectedCircuit}
                />
              )}
              {activeTab === 'approved' && (
                <ApprovedCircuits circuits={circuits.approved} />
              )}
              {activeTab === 'deployed' && (
                <DeployedCircuits circuits={circuits.deployed} />
              )}
              {activeTab === 'retired' && (
                <RetiredCircuits circuits={circuits.retired} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface PendingCircuitsProps {
  circuits: Circuit[];
  onApprove: (circuit: Circuit) => void;
}

function PendingCircuits({ circuits, onApprove }: PendingCircuitsProps) {
  if (circuits.length === 0) {
    return <EmptyState message="No pending circuits" />;
  }
  
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Type</th>
          <th>ID</th>
          <th>Signatures</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {circuits.map((circuit) => (
          <tr key={circuit.id}>
            <td>
              <span className="badge badge-warning">
                {circuit.type.split('.').pop() || circuit.type}
              </span>
              <div className="circuit-desc">{circuit.description}</div>
            </td>
            <td>
              <code>{circuit.id}</code>
            </td>
            <td>
              <span className="sig-count">
                {circuit.signatures?.length || 0} / {circuit.requiredSignatures || 2}
              </span>
            </td>
            <td>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => onApprove(circuit)}
              >
                Approve via Spark
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Approved circuits - ready for deployment
function ApprovedCircuits({ circuits }: { circuits: Circuit[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  
  if (circuits.length === 0) {
    return <EmptyState message="No approved circuits awaiting deployment" />;
  }
  
  return (
    <div className="approved-circuits">
      {circuits.map((circuit) => {
        const circuitType = circuit.type?.split('.').pop() || circuit.type || 'Unknown';
        const sigCount = Array.isArray(circuit.signatures) ? circuit.signatures.length : 0;
        const reqSigs = circuit.requiredSignatures || 1;
        
        return (
          <div key={circuit.id} className="circuit-card">
            <div 
              className="circuit-header"
              onClick={() => setExpandedId(expandedId === circuit.id ? null : circuit.id)}
            >
              <div className="circuit-main">
                <span className="badge badge-success">{circuitType}</span>
                <code className="circuit-id">{circuit.id}</code>
                {circuit.version && (
                  <span className="version-badge">{formatVersion(circuit.version)}</span>
                )}
              </div>
              <div className="circuit-meta">
                <span className="sig-count" title="Signatures collected">
                  ✓ {sigCount} / {reqSigs}
                </span>
                <span className="status-badge status-approved">Ready to Deploy</span>
                <span className="expand-icon">{expandedId === circuit.id ? '▼' : '▶'}</span>
              </div>
            </div>
            
            {circuit.description && (
              <p className="circuit-description">{circuit.description}</p>
            )}
            
            {expandedId === circuit.id && (
              <div className="signatures-panel">
                <h4>ML-DSA-87 Signatures</h4>
                <SignaturesList signatures={circuit.signatures} />
                <div className="circuit-actions">
                  <button className="btn btn-primary btn-sm">Deploy Now</button>
                  <button className="btn btn-secondary btn-sm">View Details</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Deployed circuits - currently running on nodes
function DeployedCircuits({ circuits }: { circuits: Circuit[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  
  if (circuits.length === 0) {
    return <EmptyState message="No deployed circuits" />;
  }
  
  return (
    <div className="deployed-circuits">
      {circuits.map((circuit) => {
        const circuitType = circuit.type?.split('.').pop() || circuit.type || 'Unknown';
        const upgradeAvailable = hasUpgradeAvailable(circuit);
        const nodeProgress = circuit.nodeCount 
          ? `${circuit.nodesUpToDate || 0}/${circuit.nodeCount} nodes`
          : null;
        
        return (
          <div key={circuit.id} className={`circuit-card ${upgradeAvailable ? 'has-upgrade' : ''}`}>
            <div 
              className="circuit-header"
              onClick={() => setExpandedId(expandedId === circuit.id ? null : circuit.id)}
            >
              <div className="circuit-main">
                <span className="badge badge-primary">{circuitType}</span>
                <code className="circuit-id">{circuit.id}</code>
                <span className="version-badge">{formatVersion(circuit.version)}</span>
                {upgradeAvailable && (
                  <span className="upgrade-badge" title={`Upgrade to ${formatVersion(circuit.latestVersion)}`}>
                    ↑ {formatVersion(circuit.latestVersion)}
                  </span>
                )}
              </div>
              <div className="circuit-meta">
                {nodeProgress && (
                  <span className="node-count" title="Nodes running this version">
                    {nodeProgress}
                  </span>
                )}
                <span className="status-badge status-deployed">Running</span>
                <span className="expand-icon">{expandedId === circuit.id ? '▼' : '▶'}</span>
              </div>
            </div>
            
            {circuit.description && (
              <p className="circuit-description">{circuit.description}</p>
            )}
            
            {expandedId === circuit.id && (
              <div className="details-panel">
                <div className="detail-row">
                  <span className="label">Deployed:</span>
                  <span>{circuit.deployedAt ? new Date(circuit.deployedAt).toLocaleString() : formatTimeAgo(new Date(circuit.createdAt).getTime())}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Version:</span>
                  <span>{formatVersion(circuit.version)}</span>
                </div>
                {circuit.latestVersion && upgradeAvailable && (
                  <div className="detail-row upgrade-row">
                    <span className="label">Latest:</span>
                    <span>{formatVersion(circuit.latestVersion)}</span>
                    <button className="btn btn-warning btn-sm">Upgrade</button>
                  </div>
                )}
                <div className="circuit-actions">
                  <button className="btn btn-secondary btn-sm">View Logs</button>
                  <button className="btn btn-secondary btn-sm">Node Status</button>
                  <button className="btn btn-danger btn-sm">Retire</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Retired circuits - no longer active
function RetiredCircuits({ circuits }: { circuits: Circuit[] }) {
  if (circuits.length === 0) {
    return <EmptyState message="No retired circuits" />;
  }
  
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Circuit</th>
          <th>Version</th>
          <th>Retired</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {circuits.map((circuit) => (
          <tr key={circuit.id} className="retired-row">
            <td>
              <span className="badge badge-muted">
                {circuit.type?.split('.').pop() || circuit.type}
              </span>
              <code className="circuit-id">{circuit.id}</code>
            </td>
            <td>
              <span className="version-badge">{formatVersion(circuit.version)}</span>
            </td>
            <td>{circuit.retiredAt ? formatTimeAgo(new Date(circuit.retiredAt).getTime()) : 'Unknown'}</td>
            <td>
              <button className="btn btn-secondary btn-sm">History</button>
              <button className="btn btn-secondary btn-sm">Re-deploy</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Reusable signatures list component
function SignaturesList({ signatures }: { signatures?: Signature[] }) {
  if (!signatures || signatures.length === 0) {
    return <p className="no-signatures">No signatures yet</p>;
  }
  
  return (
    <div className="signatures-list">
      {signatures.map((sig, idx) => {
        if (!sig) return null;
        
        const signerHash = sig.signerKeyHash ? String(sig.signerKeyHash).slice(0, 16) : 'Unknown';
        const sigPreview = sig.signature ? String(sig.signature).slice(0, 24) : 'N/A';
        const pubKeyPreview = sig.publicKey ? String(sig.publicKey).slice(0, 24) : null;
        const pubKeyLen = sig.publicKey ? String(sig.publicKey).length : 0;
        
        return (
          <div key={idx} className="signature-item">
            <div className="sig-header">
              <span className={`sig-status ${sig.verified ? 'verified' : 'unverified'}`}>
                {sig.verified ? '✓ Verified' : '○ Unverified'}
              </span>
              <span className="sig-algo">{sig.algorithm || 'ML-DSA-87'}</span>
            </div>
            <div className="sig-details">
              <div className="sig-row">
                <span className="label">Signer:</span>
                <code>{signerHash}...</code>
              </div>
              <div className="sig-row">
                <span className="label">Signed:</span>
                <span>{sig.approvedAt ? new Date(sig.approvedAt).toLocaleString() : 'Unknown'}</span>
              </div>
              <div className="sig-row">
                <span className="label">Signature:</span>
                <code className="sig-preview">{sigPreview}...</code>
              </div>
              {pubKeyPreview && (
                <div className="sig-row">
                  <span className="label">Public Key:</span>
                  <code className="sig-preview">{pubKeyPreview}...</code>
                  <span className="key-size">({pubKeyLen} chars)</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="empty-state">
      <span className="empty-icon">📦</span>
      <p>{message}</p>
    </div>
  );
}

function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'Just now';
}

// Template Source View Modal
interface TemplateSourceModalProps {
  template: CircuitTemplate;
  onClose: () => void;
  onDeploy: () => void;
}

function TemplateSourceModal({ template, onClose, onDeploy }: TemplateSourceModalProps) {
  const [validationStatus, setValidationStatus] = useState<'valid' | 'invalid' | 'loading'>('loading');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  
  // Generate ESCIR YAML from template
  const escirYaml = generateEscirYaml(template);
  
  // Simulate schema validation
  useEffect(() => {
    const validateSchema = async () => {
      // Simulate validation delay
      await new Promise(r => setTimeout(r, 500));
      
      // Check for common issues
      const errors: string[] = [];
      
      if (!template.id) errors.push('Missing circuit ID');
      if (!template.name) errors.push('Missing circuit name');
      if (!template.category) errors.push('Missing category');
      if (!template.version) errors.push('Missing version');
      
      if (errors.length > 0) {
        setValidationErrors(errors);
        setValidationStatus('invalid');
      } else {
        setValidationStatus('valid');
      }
    };
    
    validateSchema();
  }, [template]);
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="source-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <h3>ESCIR Template Source</h3>
            <span className="template-name">{template.name}</span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {/* Validation Status */}
          <div className={`validation-status status-${validationStatus}`}>
            {validationStatus === 'loading' && (
              <>
                <span className="status-icon">⏳</span>
                <span>Validating schema...</span>
              </>
            )}
            {validationStatus === 'valid' && (
              <>
                <span className="status-icon">✓</span>
                <span>Schema valid - ESCIR v0.5.0</span>
              </>
            )}
            {validationStatus === 'invalid' && (
              <>
                <span className="status-icon">✗</span>
                <span>Validation errors:</span>
                <ul className="error-list">
                  {validationErrors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
          
          {/* ESCIR Source with syntax highlighting */}
          <div className="source-container">
            <div className="source-header">
              <span className="filename">{template.id}.escir.yaml</span>
              <button 
                className="btn-copy"
                onClick={() => navigator.clipboard.writeText(escirYaml)}
              >
                Copy
              </button>
            </div>
            <pre className="source-code yaml">
              <code>{escirYaml}</code>
            </pre>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
          <button 
            className="btn-primary"
            onClick={onDeploy}
            disabled={validationStatus === 'invalid'}
          >
            Deploy This Circuit
          </button>
        </div>
      </div>
    </div>
  );
}

// Generate ESCIR YAML from template
function generateEscirYaml(template: CircuitTemplate): string {
  const categoryPath = template.category === 'subscription' ? 'subscription' :
                       template.category === 'crypto' ? 'crypto' :
                       template.category === 'governance' ? 'governance' :
                       template.category === 'consensus' ? 'consensus' :
                       template.category === 'network' ? 'network' :
                       template.category === 'sync' ? 'sync' :
                       template.category === 'wire' ? 'wire' :
                       template.category === 'witness' ? 'witness' : 'misc';
  
  return `# =============================================================================
# ${template.name} Circuit (ESCIR v0.5.0)
# =============================================================================
# Category: ${template.category}
# Description: ${template.description}
# =============================================================================

escir: v0.5.0
name: ${template.id}
version: 1

metadata:
  circuit_id: ESC-${template.category.toUpperCase()}-${template.id.toUpperCase().replace(/-/g, '')}-001
  category: ${template.category}
  description: ${template.description}
  author: estream-dev
  priority: P2
  
  timing:
    target_latency: 20
    target_fmax: ${template.fmax || 500}
    pipeline_ii: 1
    
  resources:
    luts: ${template.luts || 200}
    ffs: ${Math.round((template.luts || 200) * 0.4)}
    brams: ${template.luts && template.luts > 500 ? 2 : 1}
    dsps: 0

# =============================================================================
# Types
# =============================================================================

types:
  CircuitInput:
    kind: struct
    fields:
      data: bytes
      timestamp: u64
    size: 40

# =============================================================================
# Inputs
# =============================================================================

inputs:
  - name: input
    type: CircuitInput
    description: Circuit input data

# =============================================================================
# Compute Pipeline
# =============================================================================

compute:
  - name: result
    expr: true
    description: Placeholder computation

# =============================================================================
# Output
# =============================================================================

output: result

# =============================================================================
# Invariants
# =============================================================================

invariants:
  - name: output_defined
    expr: result != undefined
    description: Output must be defined

# =============================================================================
# Timing Annotations
# =============================================================================

timing_annotations:
  constant_time: true
  no_early_exit: true
  fixed_latency: true

# Template source: circuits/${categoryPath}/${template.id}/circuit.v050.escir.yaml
`;
}

// Template Deploy Modal
interface TemplateDeployModalProps {
  template: CircuitTemplate;
  onClose: () => void;
}

function TemplateDeployModal({ template, onClose }: TemplateDeployModalProps) {
  const [latticeId, setLatticeId] = useState('');
  const [customConfig, setCustomConfig] = useState('{}');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<'config' | 'confirm'>('config');
  
  const handleDeploy = async () => {
    setSubmitting(true);
    
    try {
      const response = await fetch(`${EDGE_URL}/api/circuits/deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: template.id,
          lattice_id: latticeId || undefined,
          config: customConfig ? JSON.parse(customConfig) : {},
        }),
      });
      
      if (response.ok) {
        setStep('confirm');
      } else {
        console.error('Deploy failed:', await response.text());
        // Still show confirm for demo
        setStep('confirm');
      }
    } catch (error) {
      console.error('Deploy error:', error);
      // Still show confirm for demo
      setStep('confirm');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="deploy-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <h3>Deploy Circuit</h3>
            <span className="template-name">{template.name}</span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        {step === 'config' ? (
          <>
            <div className="modal-body">
              <div className="template-info">
                <div className="info-row">
                  <span className="label">Template:</span>
                  <span className="value">{template.id}</span>
                </div>
                <div className="info-row">
                  <span className="label">Version:</span>
                  <span className="value">v{template.version}</span>
                </div>
                <div className="info-row">
                  <span className="label">Category:</span>
                  <span className="value">{template.category}</span>
                </div>
                {template.luts && (
                  <div className="info-row">
                    <span className="label">Resources:</span>
                    <span className="value">{template.luts} LUTs @ {template.fmax} MHz</span>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label>Target Lattice (optional)</label>
                <select
                  value={latticeId}
                  onChange={e => setLatticeId(e.target.value)}
                >
                  <option value="">Auto-select</option>
                  <option value="lattice-alpha">alpha-devnet-lattice</option>
                  <option value="lattice-beta">beta-devnet-lattice</option>
                </select>
                <p className="help-text">Leave empty to auto-select the best available lattice</p>
              </div>
              
              <div className="form-group">
                <label>Configuration (JSON)</label>
                <textarea
                  value={customConfig}
                  onChange={e => setCustomConfig(e.target.value)}
                  rows={4}
                  placeholder='{ "option": "value" }'
                />
                <p className="help-text">Circuit-specific configuration options</p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Cancel</button>
              <button 
                className="btn-primary"
                onClick={handleDeploy}
                disabled={submitting}
              >
                {submitting ? 'Deploying...' : 'Deploy Circuit'}
              </button>
            </div>
          </>
        ) : (
          <div className="confirm-content">
            <div className="success-icon">✓</div>
            <h4>Deployment Initiated</h4>
            <p>
              The <strong>{template.name}</strong> circuit has been submitted for governance approval.
            </p>
            <p className="info-text">
              Key holders will need to approve this deployment before it becomes active.
              You can track the status in the Pending tab.
            </p>
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
